import type { Firestore } from 'firebase/firestore';
import type { CommandWriter } from './commands';
import { echoOverlay, pendingCommands, type EchoItem } from './overlay';
import { watchDevEchoes, type DevEcho } from './queries';
import type { StaffSession } from './auth';
import { createStore, type ReadableStore } from './store';

export interface EchoViewState {
  /** Pending echoes first (made on this device, not yet in Firestore), then the server's, newest first. */
  echoes: readonly EchoItem[];
  loading: boolean;
  error: string | null;
}

export interface EchoView extends ReadableStore<EchoViewState> {
  start(): void;
  dispose(): void;
}

/** Phase 0 proof of the pipeline: `devEchoes` from Firestore with the optimistic overlay on top. */
export function createEchoView(deps: {
  db: Firestore;
  writer: CommandWriter;
  session: StaffSession;
}): EchoView {
  const { db, writer, session } = deps;
  let server: readonly DevEcho[] = [];
  let loading = true;
  let error: string | null = null;
  const store = createStore<EchoViewState>({ echoes: [], loading: true, error: null });
  const cleanups: (() => void)[] = [];
  let stopQuery: (() => void) | undefined;
  let queryUid: string | null = null;

  const recompute = (): void => {
    const base = {
      echoes: server.map<EchoItem>((e) => ({
        id: e.id,
        message: e.message,
        occurredAt: e.occurredAt ?? '',
        pending: false,
      })),
    };
    const merged = echoOverlay.applyOverlay(base, pendingCommands(writer.getSnapshot()));
    store.set({ echoes: merged.echoes, loading, error });
  };

  const watch = (): void => {
    const { uid, isStaff } = session.getSnapshot();
    const wanted = isStaff ? uid : null;
    if (wanted === queryUid) return;
    stopQuery?.();
    stopQuery = undefined;
    queryUid = wanted;
    server = [];
    error = null;
    loading = wanted !== null;
    if (wanted !== null) {
      stopQuery = watchDevEchoes(
        db,
        wanted,
        (echoes) => {
          server = echoes;
          loading = false;
          error = null;
          recompute();
        },
        (err) => {
          loading = false;
          error = err.message;
          recompute();
        },
      );
    }
    recompute();
  };

  return {
    subscribe: store.subscribe,
    getSnapshot: store.getSnapshot,
    start() {
      if (cleanups.length > 0) return;
      cleanups.push(writer.subscribe(recompute), session.subscribe(watch), () => stopQuery?.());
      watch();
    },
    dispose() {
      for (const c of cleanups.splice(0)) c();
      stopQuery = undefined;
      queryUid = null;
    },
  };
}
